

import { mutatePartners } from "@/api/partner/usePartnerQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { PartnerCreateForm } from "@/features/partner/form/createForm";
import { PartnerDetailForm } from "@/features/partner/form/detailForm";
import { PartnerUpdateForm } from "@/features/partner/form/updateForm";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import { ActionHeader } from "@/features/standardLayout/StandardInfoActionHeader";
import { flexGrowable, flexStable, StandardLayoutTableWrapper } from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { partnerStatusColor, setPartnerStatus } from "@/utils/scmDataStatus";
import { ActionIcon, Avatar, Badge, Flex, Text, Tooltip } from "@mantine/core";
import { MasterApiPartnersPartnerIdDeleteRequest, PartnersGet200Response, PartnersGet200ResponseRowsInner } from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconFilterPlus, IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Column, SelectColumn } from "react-data-grid";


// 우리 회사에서 다루는 협력사에 대해 조회할 수 있는 페이지 입니다.
export const PartnerPage = () => {
  const { openModal, closeModal } = useModal();
  const { selectedRows, refetch } = useStandardLayout();
  const { mutate: deleteMutate } = useMutation(
    (params: MasterApiPartnersPartnerIdDeleteRequest) =>
      mutatePartners.delete(params).mutationFn(params as MasterApiPartnersPartnerIdDeleteRequest | any),
    {
      onSuccess: () => {
        customAlert("협력사가 성공적으로 삭제되었습니다.", "삭제 성공", "green");
        refetch();
      },
      onError: (error: AxiosError<Error>) => {
        customAlert(error.response?.data?.message ?? "협력사 삭제에 실패하였습니다.", "삭제 실패", "red");
      }
    }
  );

  const columns: readonly Column<PartnersGet200ResponseRowsInner>[] = [
    {
      ...SelectColumn,
      resizable: true,
    },
    {
      key: "code",
      name: "협력사코드",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() => openModal(
              <PartnerDetailForm partnerCode={formatterProps?.row?.code} />,
              null,
              "협력사 상세",
            )}
          >
            {formatterProps.row.code}
          </LinkAnchor>
        );
      }
    },
    {
      key: "name",
      name: "협력사명",
      sortable: true,
      resizable: true,
    },
    {
      key: "type",
      name: "협력사 타입",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Badge
            color={partnerStatusColor[formatterProps.row.type as keyof typeof partnerStatusColor]}
          >
            {setPartnerStatus(formatterProps.row.type ?? '')}
          </Badge>
        )
      }
    },
    {
      key: "contact",
      name: "연락처",
      sortable: true,
      resizable: true,
    },
    {
      key: "options",
      name: "액션",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        const updatePartnerActionView = () => {
          openModal(
            <PartnerUpdateForm formatterProps={formatterProps.row} />,
            null,
            "협력사 수정",
            true
          );
        }

        const deletePartnerAction = () => {
          openModal(
            <ConfirmForm
              message="선택한 행을 삭제하시겠습니까?"
              yesCallback={() => deleteMutate({ partnerId: formatterProps?.row.id as number })}
              noCallback={() => { }}
              commonCallback={() => closeModal(null)}
            />,
            null,
            "협력사 삭제",
          );
        }

        return (
          <Flex w="100%" h="100%" justify="center" align="center" gap="md">
            <ActionIcon
              variant="subtle"
              onClick={updatePartnerActionView}
              color="yellow"
            >
              <IconEdit />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              onClick={() => deletePartnerAction()}
              color="red"
            >
              <IconTrash />
            </ActionIcon>
          </Flex>
        );
      },
    },
  ];

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert("삭제할 협력사를 선택해주세요.", "삭제 실패", "red");
      return;
    }

    openModal(
      <ConfirmForm
        message="선택한 행을 삭제하시겠습니까?"
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ partnerId: row });
          });

          selectedRows.clear();
        }
        }
        noCallback={() => { }}
        commonCallback={() => closeModal(null)}
      />,
      null,
      "협력사 삭제",
    )
  }

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="space-between" rowGap="md">
        <ActionHeader createFn={() => openModal(<PartnerCreateForm code={""} />, null, "협력사 추가", true)} createDataTitle="협력사" deleteFn={() => handleDelete()} />
      </Flex>
      <Flex bg="white" px="sm" direction="column" gap="xs">
        <Text fz="xl" fw={600}>협력사 리스트</Text>
        <Text fz="xs">우리 회사에서 다루는 협력사에 대해 조회합니다.</Text>
      </Flex>
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={"사용자 정의 필터입니다. \"필터 추가\" 또는 \"정렬 추가\" 버튼을 클릭하여 조건을 입력합니다."}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<PartnersGet200Response, PartnersGet200ResponseRowsInner> columns={columns} />
        </StandardLayoutTableWrapper>
        <PaginationBox<PartnersGet200Response> />
      </Flex>
    </Flex>
  );
};
